import { useState, useEffect } from "react";

let cachedData = null;

export const CATEGORY_COLORS = {
  Languages: "#67c9cc",
  Frontend: "#e8956d",
  Backend: "#b08fc7",
  DevOps: "#6dbc8a",
  Tools: "#8fa3b1",
};

const TOPIC_MAP = {
  react: { id: "react", name: "React", category: "Frontend" },
  nextjs: { id: "nextjs", name: "Next.js", category: "Frontend" },
  vue: { id: "vue", name: "Vue.js", category: "Frontend" },
  angular: { id: "angular", name: "Angular", category: "Frontend" },
  tailwindcss: { id: "tailwindcss", name: "Tailwind CSS", category: "Frontend" },
  threejs: { id: "threejs", name: "Three.js", category: "Frontend" },
  typescript: { id: "typescript", name: "TypeScript", category: "Languages" },
  nodejs: { id: "nodejs", name: "Node.js", category: "Backend" },
  express: { id: "express", name: "Express", category: "Backend" },
  django: { id: "django", name: "Django", category: "Backend" },
  flask: { id: "flask", name: "Flask", category: "Backend" },
  fastapi: { id: "fastapi", name: "FastAPI", category: "Backend" },
  graphql: { id: "graphql", name: "GraphQL", category: "Backend" },
  mongodb: { id: "mongodb", name: "MongoDB", category: "Backend" },
  postgresql: { id: "postgresql", name: "PostgreSQL", category: "Backend" },
  firebase: { id: "firebase", name: "Firebase", category: "Backend" },
  docker: { id: "docker", name: "Docker", category: "DevOps" },
  kubernetes: { id: "kubernetes", name: "Kubernetes", category: "DevOps" },
  "github-actions": { id: "github-actions", name: "GitHub Actions", category: "DevOps" },
  vercel: { id: "vercel", name: "Vercel", category: "DevOps" },
  aws: { id: "aws", name: "AWS", category: "DevOps" },
  git: { id: "git", name: "Git", category: "Tools" },
  webpack: { id: "webpack", name: "Webpack", category: "Tools" },
  vite: { id: "vite", name: "Vite", category: "Tools" },
};

const LANGUAGE_CATEGORY_MAP = {
  JavaScript: "Languages", TypeScript: "Languages", Python: "Languages",
  Java: "Languages", "C++": "Languages", "C#": "Languages", C: "Languages",
  Go: "Languages", Rust: "Languages", Swift: "Languages", Kotlin: "Languages",
  Ruby: "Languages", PHP: "Languages", Dart: "Languages", Scala: "Languages",
  R: "Languages", "Jupyter Notebook": "Languages",
  CSS: "Frontend", HTML: "Frontend", SCSS: "Frontend", Sass: "Frontend",
  Shell: "DevOps", Dockerfile: "DevOps",
};

const MIN_LANG_BYTES = 10000;

// Fetch & aggregate directly from GitHub REST API (used in local dev)
async function fetchFromGitHubDirectly(username) {
  const token = process.env.REACT_APP_GITHUB_TOKEN;
  const headers = {
    Accept: "application/vnd.github.mercy-preview+json",
    "User-Agent": "portfolio-skills/1.0",
    ...(token && { Authorization: `token ${token}` }),
  };

  const reposRes = await fetch(
    `https://api.github.com/users/${username}/repos?per_page=100&type=owner`,
    { headers }
  );
  if (!reposRes.ok) throw new Error(`GitHub repos fetch failed: ${reposRes.status}`);
  const repos = await reposRes.json();

  const langMap = {};
  const topicMap = {};
  const coOccurrence = {};

  for (let i = 0; i < repos.length; i += 10) {
    const batch = repos.slice(i, i + 10);
    const results = await Promise.allSettled(
      batch.map((r) =>
        fetch(`https://api.github.com/repos/${username}/${r.name}/languages`, { headers })
          .then((res) => res.json())
      )
    );

    results.forEach((result, idx) => {
      const repo = batch[idx];
      (repo.topics || []).forEach((t) => {
        topicMap[t] = (topicMap[t] || 0) + 1;
      });

      if (result.status === "fulfilled" && result.value && !result.value.message) {
        const repoLangs = Object.keys(result.value);
        Object.entries(result.value).forEach(([lang, bytes]) => {
          langMap[lang] = (langMap[lang] || 0) + bytes;
        });

        const repoTopicSkills = (repo.topics || [])
          .filter((t) => TOPIC_MAP[t])
          .map((t) => TOPIC_MAP[t].id);
        const allInRepo = [...repoLangs.map((l) => l.toLowerCase()), ...repoTopicSkills];
        for (let a = 0; a < allInRepo.length; a++) {
          for (let b = a + 1; b < allInRepo.length; b++) {
            const key = [allInRepo[a], allInRepo[b]].sort().join("|");
            coOccurrence[key] = (coOccurrence[key] || 0) + 1;
          }
        }
      }
    });
  }

  const nodes = [];
  Object.entries(langMap).forEach(([lang, bytes]) => {
    const category = LANGUAGE_CATEGORY_MAP[lang];
    if (!category || bytes < MIN_LANG_BYTES) return;
    nodes.push({ id: lang.toLowerCase(), label: lang, category, weight: bytes });
  });
  Object.entries(topicMap).forEach(([topic, count]) => {
    const mapped = TOPIC_MAP[topic];
    if (!mapped || nodes.find((n) => n.id === mapped.id)) return;
    nodes.push({ id: mapped.id, label: mapped.name, category: mapped.category, weight: count * 15000 });
  });

  const nodeIds = new Set(nodes.map((n) => n.id));
  const links = Object.entries(coOccurrence)
    .filter(([key]) => { const [a, b] = key.split("|"); return nodeIds.has(a) && nodeIds.has(b); })
    .map(([key, value]) => { const [source, target] = key.split("|"); return { source, target, value }; });

  return { nodes, links };
}

function enrichGraphData(nodes, links) {
  if (!nodes || nodes.length === 0) throw new Error("No skill data returned from GitHub");
  const weights = nodes.map((n) => n.weight);
  const minW = Math.min(...weights);
  const maxW = Math.max(...weights);
  return {
    nodes: nodes.map((n) => ({
      ...n,
      color: CATEGORY_COLORS[n.category] || "#94a3b8",
      val: 4 + ((n.weight - minW) / (maxW - minW || 1)) * 10,
    })),
    links: links || [],
  };
}

const useGitHubSkills = () => {
  const [graphData, setGraphData] = useState(cachedData);
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cachedData) return;

    const fetchSkills = async () => {
      try {
        let nodes, links;

        if (process.env.NODE_ENV === "development") {
          // In local dev: call GitHub directly (no serverless needed)
          const username = process.env.REACT_APP_GITHUB_USERNAME || "aadityaguptaa";
          ({ nodes, links } = await fetchFromGitHubDirectly(username));
        } else {
          // In production: go through the Vercel serverless function
          const res = await fetch("/api/github");
          if (!res.ok) throw new Error(`GitHub API error: ${res.status}`);
          ({ nodes, links } = await res.json());
        }

        const enriched = enrichGraphData(nodes, links);
        cachedData = enriched;
        setGraphData(enriched);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSkills();
  }, []);

  return { graphData, loading, error };
};

export default useGitHubSkills;
